// Internationalization (i18n) system for Hindi/English support

export type Language = 'en' | 'hi';

export interface TranslationKeys {
  // Common
  common: {
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    view: string;
    create: string;
    update: string;
    submit: string;
    back: string;
    next: string;
    previous: string;
    close: string;
    confirm: string;
    yes: string;
    no: string;
    loading: string;
    error: string;
    success: string;
    warning: string;
    info: string;
    search: string;
    filter: string;
    refresh: string;
    download: string;
    upload: string;
    select: string;
    all: string;
    none: string;
    required: string;
    optional: string;
  };

  // Navigation
  navigation: {
    dashboard: string;
    complaints: string;
    events: string;
    business: string;
    charges: string;
    elections: string;
    profile: string;
    members: string;
    staff: string;
    departments: string;
    categories: string;
    cms: string;
    reports: string;
    logout: string;
    settings: string;
    help: string;
  };

  // Authentication
  auth: {
    login: string;
    logout: string;
    register: string;
    forgotPassword: string;
    resetPassword: string;
    email: string;
    password: string;
    confirmPassword: string;
    firstName: string;
    lastName: string;
    phone: string;
    rememberMe: string;
    loginSuccess: string;
    loginError: string;
    logoutSuccess: string;
    invalidCredentials: string;
    accountLocked: string;
    passwordResetSent: string;
    passwordResetSuccess: string;
  };

  // Complaints
  complaints: {
    title: string;
    description: string;
    status: string;
    priority: string;
    category: string;
    location: string;
    createdBy: string;
    assignedTo: string;
    createdAt: string;
    updatedAt: string;
    resolutionNotes: string;
    attachments: string;
    newComplaint: string;
    complaintDetails: string;
    complaintNumber: string;
    statusPending: string;
    statusAssigned: string;
    statusInProgress: string;
    statusOnHold: string;
    statusResolved: string;
    statusClosed: string;
    priorityLow: string;
    priorityMedium: string;
    priorityHigh: string;
    priorityUrgent: string;
    addNote: string;
    updateStatus: string;
    viewDetails: string;
    editComplaint: string;
    deleteComplaint: string;
    complaintSubmitted: string;
    complaintUpdated: string;
    complaintDeleted: string;
    noComplaintsFound: string;
    complaintManagement: string;
    manageComplaints: string;
    viewYourComplaints: string;
  };

  // Worker/Frontliner specific
  worker: {
    dashboard: string;
    myTasks: string;
    assignedTasks: string;
    inProgressTasks: string;
    completedTasks: string;
    startWork: string;
    markResolved: string;
    addProgressNote: string;
    holdTask: string;
    taskDetails: string;
    progressNote: string;
    describeProgress: string;
    taskCompleted: string;
    taskOnHold: string;
    overdueTasks: string;
    dueDate: string;
    estimatedResolution: string;
    welcomeBack: string;
    manageTasks: string;
    noTasksFound: string;
    taskStatusUpdated: string;
    noteAdded: string;
  };

  // Forms
  forms: {
    required: string;
    optional: string;
    invalidEmail: string;
    invalidPhone: string;
    passwordTooShort: string;
    passwordsDoNotMatch: string;
    selectCategory: string;
    enterTitle: string;
    enterDescription: string;
    enterLocation: string;
    selectPriority: string;
    uploadFiles: string;
    dragAndDrop: string;
    fileSizeLimit: string;
    supportedFormats: string;
    maxFileSize: string;
    formValidationError: string;
    formSubmissionError: string;
    formSubmissionSuccess: string;
  };

  // Status messages
  messages: {
    success: {
      complaintSubmitted: string;
      complaintUpdated: string;
      complaintDeleted: string;
      statusUpdated: string;
      noteAdded: string;
      fileUploaded: string;
      profileUpdated: string;
      passwordChanged: string;
    };
    error: {
      networkError: string;
      serverError: string;
      validationError: string;
      unauthorized: string;
      forbidden: string;
      notFound: string;
      fileUploadError: string;
      genericError: string;
    };
    info: {
      noDataFound: string;
      loadingData: string;
      processingRequest: string;
      changesSaved: string;
      dataRefreshed: string;
    };
  };

  // Time and dates
  time: {
    today: string;
    yesterday: string;
    tomorrow: string;
    thisWeek: string;
    lastWeek: string;
    thisMonth: string;
    lastMonth: string;
    thisYear: string;
    lastYear: string;
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
    weeksAgo: string;
    monthsAgo: string;
    yearsAgo: string;
  };

  // WhatsApp Bot
  whatsapp: {
    botTitle: string;
    botDescription: string;
    startChat: string;
    sendMessage: string;
    typeMessage: string;
    complaintRegistration: string;
    selectDepartment: string;
    uploadImage: string;
    complaintSubmitted: string;
    complaintNumber: string;
    thankYou: string;
    invalidInput: string;
    tryAgain: string;
    botOffline: string;
    botOnline: string;
  };

  // Reports
  reports: {
    title: string;
    dashboard: string;
    complaints: string;
    users: string;
    departments: string;
    events: string;
    businesses: string;
    charges: string;
    elections: string;
    generateReport: string;
    exportData: string;
    filterByDate: string;
    filterByDepartment: string;
    filterByStatus: string;
    weeklyReport: string;
    monthlyReport: string;
    quarterlyReport: string;
    yearlyReport: string;
    noDataAvailable: string;
    reportGenerated: string;
    reportExported: string;
  };

  // Elections
  elections: {
    title: string;
    createElection: string;
    editElection: string;
    deleteElection: string;
    electionDetails: string;
    candidates: string;
    votes: string;
    results: string;
    castVote: string;
    voteSubmitted: string;
    electionStarted: string;
    electionEnded: string;
    electionActive: string;
    electionInactive: string;
    candidateRegistered: string;
    candidateRemoved: string;
    votingOpen: string;
    votingClosed: string;
    resultsAvailable: string;
    winner: string;
    totalVotes: string;
    votePercentage: string;
  };
}

// English translations
export const enTranslations: TranslationKeys = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    create: 'Create',
    update: 'Update',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    search: 'Search',
    filter: 'Filter',
    refresh: 'Refresh',
    download: 'Download',
    upload: 'Upload',
    select: 'Select',
    all: 'All',
    none: 'None',
    required: 'Required',
    optional: 'Optional'
  },
  navigation: {
    dashboard: 'Dashboard',
    complaints: 'Complaints',
    events: 'Events',
    business: 'Business',
    charges: 'Charges',
    elections: 'Elections',
    profile: 'Profile',
    members: 'Members',
    staff: 'Staff',
    departments: 'Departments',
    categories: 'Categories',
    cms: 'CMS',
    reports: 'Reports',
    logout: 'Logout',
    settings: 'Settings',
    help: 'Help'
  },
  auth: {
    login: 'Login',
    logout: 'Logout',
    register: 'Register',
    forgotPassword: 'Forgot Password',
    resetPassword: 'Reset Password',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone',
    rememberMe: 'Remember Me',
    loginSuccess: 'Login successful',
    loginError: 'Login failed',
    logoutSuccess: 'Logout successful',
    invalidCredentials: 'Invalid credentials',
    accountLocked: 'Account locked',
    passwordResetSent: 'Password reset email sent',
    passwordResetSuccess: 'Password reset successful'
  },
  complaints: {
    title: 'Complaint Title',
    description: 'Description',
    status: 'Status',
    priority: 'Priority',
    category: 'Category',
    location: 'Location',
    createdBy: 'Created By',
    assignedTo: 'Assigned To',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    resolutionNotes: 'Resolution Notes',
    attachments: 'Attachments',
    newComplaint: 'New Complaint',
    complaintDetails: 'Complaint Details',
    complaintNumber: 'Complaint Number',
    statusPending: 'Pending',
    statusAssigned: 'Assigned',
    statusInProgress: 'In Progress',
    statusOnHold: 'On Hold',
    statusResolved: 'Resolved',
    statusClosed: 'Closed',
    priorityLow: 'Low',
    priorityMedium: 'Medium',
    priorityHigh: 'High',
    priorityUrgent: 'Urgent',
    addNote: 'Add Note',
    updateStatus: 'Update Status',
    viewDetails: 'View Details',
    editComplaint: 'Edit Complaint',
    deleteComplaint: 'Delete Complaint',
    complaintSubmitted: 'Complaint submitted successfully',
    complaintUpdated: 'Complaint updated successfully',
    complaintDeleted: 'Complaint deleted successfully',
    noComplaintsFound: 'No complaints found',
    complaintManagement: 'Complaints Management',
    manageComplaints: 'Manage all complaints in the system',
    viewYourComplaints: 'View and manage your complaints'
  },
  worker: {
    dashboard: 'Worker Dashboard',
    myTasks: 'My Tasks',
    assignedTasks: 'Assigned',
    inProgressTasks: 'In Progress',
    completedTasks: 'Completed',
    startWork: 'Start Work',
    markResolved: 'Mark Resolved',
    addProgressNote: 'Add Progress Note',
    holdTask: 'Hold',
    taskDetails: 'Task Details',
    progressNote: 'Progress Note',
    describeProgress: 'Describe your progress on this complaint...',
    taskCompleted: 'Task completed successfully',
    taskOnHold: 'Task put on hold',
    overdueTasks: 'Overdue Tasks',
    dueDate: 'Due Date',
    estimatedResolution: 'Estimated Resolution',
    welcomeBack: 'Welcome back',
    manageTasks: 'Manage your assigned tasks',
    noTasksFound: 'No tasks found for this category',
    taskStatusUpdated: 'Task status updated successfully',
    noteAdded: 'Note added successfully'
  },
  forms: {
    required: 'This field is required',
    optional: 'This field is optional',
    invalidEmail: 'Please enter a valid email address',
    invalidPhone: 'Please enter a valid phone number',
    passwordTooShort: 'Password must be at least 8 characters',
    passwordsDoNotMatch: 'Passwords do not match',
    selectCategory: 'Please select a category',
    enterTitle: 'Please enter a title',
    enterDescription: 'Please enter a description',
    enterLocation: 'Please enter a location',
    selectPriority: 'Please select a priority',
    uploadFiles: 'Upload files',
    dragAndDrop: 'Drag and drop files here',
    fileSizeLimit: 'File size limit exceeded',
    supportedFormats: 'Supported formats: PNG, JPG, PDF, TXT',
    maxFileSize: 'Maximum file size: 5MB',
    formValidationError: 'Please fix the validation errors',
    formSubmissionError: 'Failed to submit form',
    formSubmissionSuccess: 'Form submitted successfully'
  },
  messages: {
    success: {
      complaintSubmitted: 'Complaint submitted successfully',
      complaintUpdated: 'Complaint updated successfully',
      complaintDeleted: 'Complaint deleted successfully',
      statusUpdated: 'Status updated successfully',
      noteAdded: 'Note added successfully',
      fileUploaded: 'File uploaded successfully',
      profileUpdated: 'Profile updated successfully',
      passwordChanged: 'Password changed successfully'
    },
    error: {
      networkError: 'Network error. Please check your connection',
      serverError: 'Server error. Please try again later',
      validationError: 'Validation error. Please check your input',
      unauthorized: 'You are not authorized to perform this action',
      forbidden: 'Access forbidden',
      notFound: 'Resource not found',
      fileUploadError: 'File upload failed',
      genericError: 'An error occurred. Please try again'
    },
    info: {
      noDataFound: 'No data found',
      loadingData: 'Loading data...',
      processingRequest: 'Processing request...',
      changesSaved: 'Changes saved',
      dataRefreshed: 'Data refreshed'
    }
  },
  time: {
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    lastYear: 'Last Year',
    justNow: 'Just now',
    minutesAgo: 'minutes ago',
    hoursAgo: 'hours ago',
    daysAgo: 'days ago',
    weeksAgo: 'weeks ago',
    monthsAgo: 'months ago',
    yearsAgo: 'years ago'
  },
  whatsapp: {
    botTitle: 'WhatsApp Bot',
    botDescription: 'Register complaints via WhatsApp',
    startChat: 'Start Chat',
    sendMessage: 'Send Message',
    typeMessage: 'Type your message...',
    complaintRegistration: 'Complaint Registration',
    selectDepartment: 'Select Department',
    uploadImage: 'Upload Image',
    complaintSubmitted: 'Complaint submitted successfully',
    complaintNumber: 'Your complaint number is',
    thankYou: 'Thank you for your complaint',
    invalidInput: 'Invalid input. Please try again',
    tryAgain: 'Try Again',
    botOffline: 'Bot is offline',
    botOnline: 'Bot is online'
  },
  reports: {
    title: 'Reports & Analytics',
    dashboard: 'Dashboard',
    complaints: 'Complaints',
    users: 'Users',
    departments: 'Departments',
    events: 'Events',
    businesses: 'Businesses',
    charges: 'Charges',
    elections: 'Elections',
    generateReport: 'Generate Report',
    exportData: 'Export Data',
    filterByDate: 'Filter by Date',
    filterByDepartment: 'Filter by Department',
    filterByStatus: 'Filter by Status',
    weeklyReport: 'Weekly Report',
    monthlyReport: 'Monthly Report',
    quarterlyReport: 'Quarterly Report',
    yearlyReport: 'Yearly Report',
    noDataAvailable: 'No data available',
    reportGenerated: 'Report generated successfully',
    reportExported: 'Report exported successfully'
  },
  elections: {
    title: 'Elections',
    createElection: 'Create Election',
    editElection: 'Edit Election',
    deleteElection: 'Delete Election',
    electionDetails: 'Election Details',
    candidates: 'Candidates',
    votes: 'Votes',
    results: 'Results',
    castVote: 'Cast Vote',
    voteSubmitted: 'Vote submitted successfully',
    electionStarted: 'Election started',
    electionEnded: 'Election ended',
    electionActive: 'Active',
    electionInactive: 'Inactive',
    candidateRegistered: 'Candidate registered',
    candidateRemoved: 'Candidate removed',
    votingOpen: 'Voting is open',
    votingClosed: 'Voting is closed',
    resultsAvailable: 'Results are available',
    winner: 'Winner',
    totalVotes: 'Total Votes',
    votePercentage: 'Vote Percentage'
  }
};

// Hindi translations
export const hiTranslations: TranslationKeys = {
  common: {
    save: 'सहेजें',
    cancel: 'रद्द करें',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    view: 'देखें',
    create: 'बनाएं',
    update: 'अपडेट करें',
    submit: 'जमा करें',
    back: 'वापस',
    next: 'अगला',
    previous: 'पिछला',
    close: 'बंद करें',
    confirm: 'पुष्टि करें',
    yes: 'हाँ',
    no: 'नहीं',
    loading: 'लोड हो रहा है...',
    error: 'त्रुटि',
    success: 'सफलता',
    warning: 'चेतावनी',
    info: 'जानकारी',
    search: 'खोजें',
    filter: 'फिल्टर',
    refresh: 'रिफ्रेश करें',
    download: 'डाउनलोड',
    upload: 'अपलोड',
    select: 'चुनें',
    all: 'सभी',
    none: 'कोई नहीं',
    required: 'आवश्यक',
    optional: 'वैकल्पिक'
  },
  navigation: {
    dashboard: 'डैशबोर्ड',
    complaints: 'शिकायतें',
    events: 'कार्यक्रम',
    business: 'व्यापार',
    charges: 'शुल्क',
    elections: 'चुनाव',
    profile: 'प्रोफाइल',
    members: 'सदस्य',
    staff: 'कर्मचारी',
    departments: 'विभाग',
    categories: 'श्रेणियां',
    cms: 'सीएमएस',
    reports: 'रिपोर्ट्स',
    logout: 'लॉगआउट',
    settings: 'सेटिंग्स',
    help: 'सहायता'
  },
  auth: {
    login: 'लॉगिन',
    logout: 'लॉगआउट',
    register: 'रजिस्टर',
    forgotPassword: 'पासवर्ड भूल गए',
    resetPassword: 'पासवर्ड रीसेट',
    email: 'ईमेल',
    password: 'पासवर्ड',
    confirmPassword: 'पासवर्ड की पुष्टि करें',
    firstName: 'पहला नाम',
    lastName: 'अंतिम नाम',
    phone: 'फोन',
    rememberMe: 'मुझे याद रखें',
    loginSuccess: 'लॉगिन सफल',
    loginError: 'लॉगिन असफल',
    logoutSuccess: 'लॉगआउट सफल',
    invalidCredentials: 'अमान्य क्रेडेंशियल्स',
    accountLocked: 'खाता लॉक',
    passwordResetSent: 'पासवर्ड रीसेट ईमेल भेजा गया',
    passwordResetSuccess: 'पासवर्ड रीसेट सफल'
  },
  complaints: {
    title: 'शिकायत का शीर्षक',
    description: 'विवरण',
    status: 'स्थिति',
    priority: 'प्राथमिकता',
    category: 'श्रेणी',
    location: 'स्थान',
    createdBy: 'द्वारा बनाया गया',
    assignedTo: 'असाइन किया गया',
    createdAt: 'बनाया गया',
    updatedAt: 'अपडेट किया गया',
    resolutionNotes: 'समाधान नोट्स',
    attachments: 'अटैचमेंट्स',
    newComplaint: 'नई शिकायत',
    complaintDetails: 'शिकायत विवरण',
    complaintNumber: 'शिकायत संख्या',
    statusPending: 'लंबित',
    statusAssigned: 'असाइन किया गया',
    statusInProgress: 'प्रगति में',
    statusOnHold: 'रोक दिया गया',
    statusResolved: 'हल किया गया',
    statusClosed: 'बंद किया गया',
    priorityLow: 'कम',
    priorityMedium: 'मध्यम',
    priorityHigh: 'उच्च',
    priorityUrgent: 'तत्काल',
    addNote: 'नोट जोड़ें',
    updateStatus: 'स्थिति अपडेट करें',
    viewDetails: 'विवरण देखें',
    editComplaint: 'शिकायत संपादित करें',
    deleteComplaint: 'शिकायत हटाएं',
    complaintSubmitted: 'शिकायत सफलतापूर्वक जमा की गई',
    complaintUpdated: 'शिकायत सफलतापूर्वक अपडेट की गई',
    complaintDeleted: 'शिकायत सफलतापूर्वक हटाई गई',
    noComplaintsFound: 'कोई शिकायत नहीं मिली',
    complaintManagement: 'शिकायत प्रबंधन',
    manageComplaints: 'सिस्टम में सभी शिकायतों का प्रबंधन करें',
    viewYourComplaints: 'अपनी शिकायतें देखें और प्रबंधित करें'
  },
  worker: {
    dashboard: 'कर्मचारी डैशबोर्ड',
    myTasks: 'मेरे कार्य',
    assignedTasks: 'असाइन किए गए',
    inProgressTasks: 'प्रगति में',
    completedTasks: 'पूर्ण',
    startWork: 'काम शुरू करें',
    markResolved: 'हल के रूप में चिह्नित करें',
    addProgressNote: 'प्रगति नोट जोड़ें',
    holdTask: 'रोकें',
    taskDetails: 'कार्य विवरण',
    progressNote: 'प्रगति नोट',
    describeProgress: 'इस शिकायत पर अपनी प्रगति का वर्णन करें...',
    taskCompleted: 'कार्य सफलतापूर्वक पूर्ण',
    taskOnHold: 'कार्य रोक दिया गया',
    overdueTasks: 'अतिदेय कार्य',
    dueDate: 'नियत तारीख',
    estimatedResolution: 'अनुमानित समाधान',
    welcomeBack: 'वापस स्वागत है',
    manageTasks: 'अपने असाइन किए गए कार्यों का प्रबंधन करें',
    noTasksFound: 'इस श्रेणी के लिए कोई कार्य नहीं मिला',
    taskStatusUpdated: 'कार्य स्थिति सफलतापूर्वक अपडेट की गई',
    noteAdded: 'नोट सफलतापूर्वक जोड़ा गया'
  },
  forms: {
    required: 'यह फ़ील्ड आवश्यक है',
    optional: 'यह फ़ील्ड वैकल्पिक है',
    invalidEmail: 'कृपया एक वैध ईमेल पता दर्ज करें',
    invalidPhone: 'कृपया एक वैध फोन नंबर दर्ज करें',
    passwordTooShort: 'पासवर्ड कम से कम 8 अक्षर का होना चाहिए',
    passwordsDoNotMatch: 'पासवर्ड मेल नहीं खाते',
    selectCategory: 'कृपया एक श्रेणी चुनें',
    enterTitle: 'कृपया एक शीर्षक दर्ज करें',
    enterDescription: 'कृपया एक विवरण दर्ज करें',
    enterLocation: 'कृपया एक स्थान दर्ज करें',
    selectPriority: 'कृपया एक प्राथमिकता चुनें',
    uploadFiles: 'फाइलें अपलोड करें',
    dragAndDrop: 'फाइलें यहां खींचें और छोड़ें',
    fileSizeLimit: 'फाइल आकार सीमा पार',
    supportedFormats: 'समर्थित प्रारूप: PNG, JPG, PDF, TXT',
    maxFileSize: 'अधिकतम फाइल आकार: 5MB',
    formValidationError: 'कृपया सत्यापन त्रुटियों को ठीक करें',
    formSubmissionError: 'फॉर्म जमा करने में विफल',
    formSubmissionSuccess: 'फॉर्म सफलतापूर्वक जमा किया गया'
  },
  messages: {
    success: {
      complaintSubmitted: 'शिकायत सफलतापूर्वक जमा की गई',
      complaintUpdated: 'शिकायत सफलतापूर्वक अपडेट की गई',
      complaintDeleted: 'शिकायत सफलतापूर्वक हटाई गई',
      statusUpdated: 'स्थिति सफलतापूर्वक अपडेट की गई',
      noteAdded: 'नोट सफलतापूर्वक जोड़ा गया',
      fileUploaded: 'फाइल सफलतापूर्वक अपलोड की गई',
      profileUpdated: 'प्रोफाइल सफलतापूर्वक अपडेट की गई',
      passwordChanged: 'पासवर्ड सफलतापूर्वक बदला गया'
    },
    error: {
      networkError: 'नेटवर्क त्रुटि। कृपया अपना कनेक्शन जांचें',
      serverError: 'सर्वर त्रुटि। कृपया बाद में पुनः प्रयास करें',
      validationError: 'सत्यापन त्रुटि। कृपया अपना इनपुट जांचें',
      unauthorized: 'आप इस क्रिया को करने के लिए अधिकृत नहीं हैं',
      forbidden: 'पहुंच निषिद्ध',
      notFound: 'संसाधन नहीं मिला',
      fileUploadError: 'फाइल अपलोड असफल',
      genericError: 'एक त्रुटि हुई। कृपया पुनः प्रयास करें'
    },
    info: {
      noDataFound: 'कोई डेटा नहीं मिला',
      loadingData: 'डेटा लोड हो रहा है...',
      processingRequest: 'अनुरोध प्रसंस्करण...',
      changesSaved: 'परिवर्तन सहेजे गए',
      dataRefreshed: 'डेटा रिफ्रेश किया गया'
    }
  },
  time: {
    today: 'आज',
    yesterday: 'कल',
    tomorrow: 'कल',
    thisWeek: 'इस सप्ताह',
    lastWeek: 'पिछले सप्ताह',
    thisMonth: 'इस महीने',
    lastMonth: 'पिछले महीने',
    thisYear: 'इस साल',
    lastYear: 'पिछले साल',
    justNow: 'अभी अभी',
    minutesAgo: 'मिनट पहले',
    hoursAgo: 'घंटे पहले',
    daysAgo: 'दिन पहले',
    weeksAgo: 'सप्ताह पहले',
    monthsAgo: 'महीने पहले',
    yearsAgo: 'साल पहले'
  },
  whatsapp: {
    botTitle: 'व्हाट्सऐप बॉट',
    botDescription: 'व्हाट्सऐप के माध्यम से शिकायत दर्ज करें',
    startChat: 'चैट शुरू करें',
    sendMessage: 'संदेश भेजें',
    typeMessage: 'अपना संदेश टाइप करें...',
    complaintRegistration: 'शिकायत पंजीकरण',
    selectDepartment: 'विभाग चुनें',
    uploadImage: 'छवि अपलोड करें',
    complaintSubmitted: 'शिकायत सफलतापूर्वक जमा की गई',
    complaintNumber: 'आपकी शिकायत संख्या है',
    thankYou: 'आपकी शिकायत के लिए धन्यवाद',
    invalidInput: 'अमान्य इनपुट। कृपया पुनः प्रयास करें',
    tryAgain: 'पुनः प्रयास करें',
    botOffline: 'बॉट ऑफलाइन है',
    botOnline: 'बॉट ऑनलाइन है'
  },
  reports: {
    title: 'रिपोर्ट्स और एनालिटिक्स',
    dashboard: 'डैशबोर्ड',
    complaints: 'शिकायतें',
    users: 'उपयोगकर्ता',
    departments: 'विभाग',
    events: 'कार्यक्रम',
    businesses: 'व्यापार',
    charges: 'शुल्क',
    elections: 'चुनाव',
    generateReport: 'रिपोर्ट जेनरेट करें',
    exportData: 'डेटा एक्सपोर्ट करें',
    filterByDate: 'तारीख के अनुसार फिल्टर',
    filterByDepartment: 'विभाग के अनुसार फिल्टर',
    filterByStatus: 'स्थिति के अनुसार फिल्टर',
    weeklyReport: 'साप्ताहिक रिपोर्ट',
    monthlyReport: 'मासिक रिपोर्ट',
    quarterlyReport: 'त्रैमासिक रिपोर्ट',
    yearlyReport: 'वार्षिक रिपोर्ट',
    noDataAvailable: 'कोई डेटा उपलब्ध नहीं',
    reportGenerated: 'रिपोर्ट सफलतापूर्वक जेनरेट की गई',
    reportExported: 'रिपोर्ट सफलतापूर्वक एक्सपोर्ट की गई'
  },
  elections: {
    title: 'चुनाव',
    createElection: 'चुनाव बनाएं',
    editElection: 'चुनाव संपादित करें',
    deleteElection: 'चुनाव हटाएं',
    electionDetails: 'चुनाव विवरण',
    candidates: 'उम्मीदवार',
    votes: 'वोट',
    results: 'परिणाम',
    castVote: 'वोट डालें',
    voteSubmitted: 'वोट सफलतापूर्वक जमा किया गया',
    electionStarted: 'चुनाव शुरू हुआ',
    electionEnded: 'चुनाव समाप्त हुआ',
    electionActive: 'सक्रिय',
    electionInactive: 'निष्क्रिय',
    candidateRegistered: 'उम्मीदवार पंजीकृत',
    candidateRemoved: 'उम्मीदवार हटाया गया',
    votingOpen: 'मतदान खुला है',
    votingClosed: 'मतदान बंद है',
    resultsAvailable: 'परिणाम उपलब्ध हैं',
    winner: 'विजेता',
    totalVotes: 'कुल वोट',
    votePercentage: 'वोट प्रतिशत'
  }
};

// Translation function
export const getTranslation = (key: string, language: Language = 'en'): string => {
  const translations = language === 'hi' ? hiTranslations : enTranslations;
  const keys = key.split('.');
  let value: any = translations;
  
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
  }
  
  return value;
};

// Language detection and storage
export const getStoredLanguage = (): Language => {
  const stored = localStorage.getItem('language');
  return (stored === 'hi' || stored === 'en') ? stored : 'en';
};

export const setStoredLanguage = (language: Language): void => {
  localStorage.setItem('language', language);
};

// RTL support for Hindi
export const isRTL = (language: Language): boolean => {
  return language === 'hi';
};

// Number formatting for different languages
export const formatNumber = (num: number, language: Language = 'en'): string => {
  if (language === 'hi') {
    return new Intl.NumberFormat('hi-IN').format(num);
  }
  return new Intl.NumberFormat('en-US').format(num);
};

// Date formatting for different languages
export const formatDate = (date: Date | string, language: Language = 'en'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (language === 'hi') {
    return new Intl.DateTimeFormat('hi-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  }
  
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj);
};

// Currency formatting
export const formatCurrency = (amount: number, language: Language = 'en'): string => {
  if (language === 'hi') {
    return new Intl.NumberFormat('hi-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR'
  }).format(amount);
};

export default {
  getTranslation,
  getStoredLanguage,
  setStoredLanguage,
  isRTL,
  formatNumber,
  formatDate,
  formatCurrency,
  enTranslations,
  hiTranslations
};
